import { FC, useEffect } from 'react';
import { Box, DropdownMenu, withStyles, ElinxType } from 'vopay';
import { useHistory, useParams } from 'react-router-dom';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';
import { UtilButton, Title, Button } from 'UI';
import { usePermissions } from '@/providers/Permissions.provider';
import useFileDownload from '@/hooks/useFiledDownload';
import useElinx from './useElinx';
import ElinxDetails from './ElinxDetails';
import styles from './ElinxDetails.module.scss';
import ApprovalPanel from '@/components/UI/ApprovalPanel/ApprovalPanel';

const cn = withStyles(styles);

const ElinxDetailsPage: FC = () => {
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const { ELinxId } = useParams<{ ELinxId: string }>();
  const { printFile, downloadPdfPage } = useFileDownload();
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { details, fetchElinxDetails, cancelElinx } = useElinx();
  const { permissions } = usePermissions();

  const getElinxDetails = async () => {
    try {
      await fetchElinxDetails({ ELinxRequestID: ELinxId });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchingError');
      alertDialog.error(t('ErrorMessages.fetchingError'), msg);
      console.error(err);
    }
  };

  useEffect(() => {
    if (!ELinxId) return;

    getElinxDetails();
  }, [ELinxId]);

  async function handleDialogue() {
    const result = await alertDialog.caution(
      t('Details.CancelElinx.title', {
        defaultValue: 'Are you sure you want to cancel eLinx Request?',
      }),
      t('Details.CancelElinx.description', {
        defaultValue: 'This action cannot be undone.',
      }),
      {
        abortLabel: tGlobal('Details.CancelElinx.cancelButton', {
          defaultValue: 'Go Back',
        }),
        confirmLabel: tGlobal('Details.CancelElinx.confirmButton', {
          defaultValue: 'Yes, Cancel eLinx Request',
        }),
      },
    );

    if (result !== 'confirm') return;

    try {
      await cancelElinx({ ELinxRequestID: ELinxId });

      const successResult = await alertDialog.success(
        t('Details.CancelElinx.successMessage', {
          defaultValue: 'The eLinx Request was successfully cancelled',
        }),
        '',
        {
          confirmLabel: t('Details.CancelElinx.successConfirmButton', {
            defaultValue: 'Back to Transaction',
          }),
        },
      );

      if (successResult === 'confirm') {
        history.goBack();
      }
    } catch (err) {
      const confirmRes = await alertDialog.caution(
        t('Details.CancelElinx.error', { defaultValue: 'Error! The eLinx Request was not cancelled.' }),
        err instanceof Error ? err.message : t('Details.unknownError', { defaultValue: 'Unknown error' }),
        {
          confirmLabel: tGlobal('Buttons.okay', { defaultValue: 'Okay' }),
        },
      );

      if (confirmRes === 'confirm') {
        history.goBack();
      }
    }
  }

  const elinxTypeMap: Record<ElinxType, string> = {
    ePay: t('Type.ePay', { defaultValue: 'ePay' }),
    eCollect: t('Type.eCollect', { defaultValue: 'eCollect' }),
    eConnect: t('Type.eConnect', { defaultValue: 'eConnect' }),
    eFlex: t('Type.eFlex', { defaultValue: 'eFlex' }),
  };

  if (!details) return null;

  return (
    <div className={cn('wrapper')}>
      <Box flx="col" gap="sm">
        <UtilButton iconLeft="chevron_left" size="large" onClick={() => history.goBack()}>
          {tGlobal('Buttons.back', { defaultValue: 'Back' })}
        </UtilButton>
        <Box jc="sb">
          <Title>{t('Details.title', { type: elinxTypeMap[details.eLinxType] })}</Title>
          <Box gapSize="8px">
            {(details.Status === 'pending' || details.Status === 'pending approval') &&
              permissions.ManageTransactions && (
                <Button onClick={handleDialogue}>
                  {t('Details.cancelButton', { defaultValue: 'Cancel eLinx Request' })}
                </Button>
              )}

            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button iconRight="keyboard_arrow_down">
                  {tGlobal('ExportDropdown.label', { defaultValue: 'Export' })}
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  onSelect={() => {
                    downloadPdfPage('eLinx-overview', t('title', { type: elinxTypeMap[details.eLinxType] }));
                  }}
                >
                  {tGlobal('ExportDropdown.Options.pdf', { defaultValue: 'PDF' })}
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={() => {
                    printFile('eLinx-overview');
                  }}
                >
                  {tGlobal('ExportDropdown.Options.print', { defaultValue: 'Print' })}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </Box>
        </Box>
      </Box>
      {details.Status === 'pending approval' && (
        <ApprovalPanel
          title={t('Approval.statusPending', { defaultValue: 'Pending Approval' })}
          description={t('Approval.description', {
            defaultValue: 'Approvers can find these transaction(s) in their Approval Inbox',
          })}
        />
      )}
      <div className={cn('cardContainer')} id="eLinx-overview">
        <ElinxDetails details={details} />
      </div>
    </div>
  );
};

export default ElinxDetailsPage;
